// apps/console/src/lib/fabricSetupScript.js  (Govern360)
// G360_K636
//
// The ONE Fabric setup script (PowerShell, for Azure Cloud Shell). Used by
// the Fabric setup drawer (existing customers) and appended to both Microsoft
// connection setup scripts (new customers), so every path runs the same steps.
//
// It runs under the signed-in ADMINISTRATOR's own sign-in. The Govern360 app
// never grants itself anything. It shows what it will do, asks Y/N, is safe
// to run again, never disables a setting and never removes anyone, and when a
// step needs rights the administrator does not have it prints the manual step
// and carries on.
//
// Steps: 1 security group + the Govern360 app in it; 2 the two Fabric tenant
// settings for that group (update API is a Microsoft Preview); 3 capacity
// Contributor role (manual - Fabric has no documented add-only API);
// 4 Cost Management Reader for the Azure connection's app on subscriptions
// that hold Fabric capacities; 5 "Check again" in Govern360.
//
// The whole script is one scriptblock (& { ... }) so it can be pasted into
// Cloud Shell in one go: PowerShell reads the entire paste before it runs, so
// the Y/N question is answered by the person, not by the next pasted line.
// ASCII only. Identifiers only - no credential is ever put in the script.

export const FABRIC_GROUP_NAME = 'Govern360 - Fabric API access';
export const FABRIC_SCRIPT_FILE = 'Setup-Govern360-Fabric.ps1';

const GUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
/* Only a well-formed GUID is ever written into the script (anything else is
   left empty and the script asks for it), so nothing can break the quoting. */
export const safeGuid = (v) => (typeof v === 'string' && GUID_RE.test(v.trim()) ? v.trim().toLowerCase() : '');

/**
 * @param {{ clientId?: string, entraTenantId?: string, azureAppId?: string, fromConnectScript?: boolean }} [opts]
 *   clientId      - the Govern360 Microsoft (Entra) app's client id
 *   entraTenantId - the customer's Entra tenant id
 *   azureAppId    - the client id of the Govern360 Azure connection's app (optional)
 *   fromConnectScript - true when run from a connection setup script: values
 *                   not given here are read from G360_APP_ID / G360_TENANT_ID
 */
export function generateFabricSetupPs1(opts) {
  const o = opts || {};
  const cid = safeGuid(o.clientId);
  const tid = safeGuid(o.entraTenantId);
  const azid = safeGuid(o.azureAppId);
  const origin = o.fromConnectScript ? 'the Microsoft connection setup' : 'Govern360 - Fabric setup';
  return `# ${FABRIC_SCRIPT_FILE}  (Azure Cloud Shell - PowerShell)
# Govern360 - Fabric capacity and AI consumption reading (${origin})
# ---------------------------------------------------------------------
# Runs under YOUR administrator sign-in. The Govern360 app never grants
# itself anything: every change below is made by you, and you are asked
# Y/N first. Safe to run again (it reuses what already exists). It never
# disables a setting and never removes a person, group or role.
# If your sign-in lacks the rights for a step, it prints the manual step
# and carries on.
# ---------------------------------------------------------------------
& {
$ErrorActionPreference = 'Continue'
$ClientId = '${cid}'
$EntraTenantId = '${tid}'
$AzureAppId = '${azid}'
$GroupName = '${FABRIC_GROUP_NAME}'
$FabricApi = 'https://api.fabric.microsoft.com/v1'
$GuidPattern = '^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$'
if (-not $ClientId -and $env:G360_APP_ID) { $ClientId = $env:G360_APP_ID }
if (-not $EntraTenantId -and $env:G360_TENANT_ID) { $EntraTenantId = $env:G360_TENANT_ID }
if (-not $AzureAppId -and $env:G360_AZURE_APP_ID) { $AzureAppId = $env:G360_AZURE_APP_ID }
$InCloudShell = [bool]($env:ACC_CLOUD -or ($env:AZUREPS_HOST_ENVIRONMENT -like 'cloud-shell*'))
$Results = [ordered]@{}
function Note([string]$Step, [string]$State, [string]$Text) {
  $Results[$Step] = $State + ' - ' + $Text
  $color = 'Yellow'
  if ($State -eq 'OK' -or $State -eq 'DONE') { $color = 'Green' }
  Write-Host ('    ' + $State + ': ' + $Text) -ForegroundColor $color
}
function Manual([string]$Text) { Write-Host ('    MANUAL: ' + $Text) -ForegroundColor Yellow }
function Short($e) { $m = [string]$e; if ($m.Length -gt 200) { $m = $m.Substring(0, 200) + '...' }; return $m }

Write-Host ''
Write-Host '==== Govern360 - Fabric capacity and AI consumption reading ====' -ForegroundColor Cyan
if (-not ($ClientId -match $GuidPattern)) { $ClientId = (Read-Host 'Govern360 app (client) ID (Connections page)').Trim() }
if (-not ($ClientId -match $GuidPattern)) { Write-Host 'That is not an app (client) ID. Nothing was changed.' -ForegroundColor Yellow; return }
if (-not ($EntraTenantId -match $GuidPattern)) { $EntraTenantId = (Read-Host 'Your Entra (directory) tenant ID').Trim() }
if (-not ($EntraTenantId -match $GuidPattern)) { Write-Host 'That is not a tenant ID. Nothing was changed.' -ForegroundColor Yellow; return }
Write-Host ('  Govern360 app (client) ID : ' + $ClientId)
Write-Host ('  Entra tenant ID           : ' + $EntraTenantId)
Write-Host ''
Write-Host 'This script will, under YOUR sign-in:'
Write-Host ('  1. Create (or reuse) the security group "' + $GroupName + '" and add the Govern360 app to it.')
Write-Host '  2. Enable, for that group only, the Fabric tenant settings "Service principals can call Fabric public APIs"'
Write-Host '     and "Service principals can access read-only admin APIs" (existing groups are kept; nothing is disabled).'
Write-Host '     Uses a Microsoft Preview API; you need the Fabric administrator role.'
Write-Host '  3. List your Fabric capacities and print where to give the group the Contributor role (you do this by hand).'
Write-Host '  4. Give the Govern360 Azure connection app "Cost Management Reader" on the subscriptions that hold'
Write-Host '     Fabric capacities (skipped if you do not give its app ID).'
Write-Host '  5. Tell you to click "Check again" in Govern360.'
Write-Host 'The Govern360 app never grants itself anything. Everything it reads stays read-only.'
$answer = Read-Host 'Continue? (Y/N)'
if (-not ($answer -match '^(y|yes)$')) { Write-Host 'Nothing was changed.'; return }

# ---- 1. Security group ------------------------------------------------------
Write-Host ''
Write-Host '-> 1. Security group' -ForegroundColor Cyan
$GroupId = $null
try {
  Import-Module Microsoft.Graph.Authentication -ErrorAction Stop
  Import-Module Microsoft.Graph.Groups -ErrorAction Stop
  Import-Module Microsoft.Graph.Applications -ErrorAction Stop
  $mg = Get-MgContext
  $need = @('Group.ReadWrite.All', 'Application.Read.All')
  $have = @(); if ($mg) { $have = @($mg.Scopes) }
  if (-not $mg -or ($need | Where-Object { $have -notcontains $_ })) {
    if ($InCloudShell) { Connect-MgGraph -TenantId $EntraTenantId -Scopes $need -UseDeviceCode -NoWelcome -ErrorAction Stop }
    else { Connect-MgGraph -TenantId $EntraTenantId -Scopes $need -NoWelcome -ErrorAction Stop }
  }
  $sp = Get-MgServicePrincipal -Filter ("appId eq '" + $ClientId + "'") -ErrorAction Stop | Select-Object -First 1
  if (-not $sp) { throw 'The Govern360 app has no service principal in this tenant yet (grant admin consent first).' }
  $grp = Get-MgGroup -Filter ("displayName eq '" + $GroupName + "'") -ErrorAction Stop | Where-Object { $_.SecurityEnabled } | Select-Object -First 1
  if ($grp) { Write-Host ('    Reusing group ' + $grp.Id) }
  else {
    $grp = New-MgGroup -DisplayName $GroupName -Description 'Lets the Govern360 app read Fabric (capacities, admin inventory). Created by the administrator.' -MailEnabled:$false -MailNickname 'govern360-fabric-api' -SecurityEnabled:$true -ErrorAction Stop
    Write-Host ('    Created group ' + $grp.Id)
  }
  $GroupId = $grp.Id
  $members = @(Get-MgGroupMember -GroupId $GroupId -All -ErrorAction Stop | ForEach-Object { $_.Id })
  if ($members -contains $sp.Id) { Note '1 Security group' 'OK' 'the Govern360 app is already in the group.' }
  else {
    New-MgGroupMember -GroupId $GroupId -DirectoryObjectId $sp.Id -ErrorAction Stop
    Note '1 Security group' 'DONE' 'the Govern360 app was added to the group.'
  }
} catch {
  Note '1 Security group' 'MANUAL' ('could not finish (' + (Short $_) + ')')
  Manual ('Entra admin center > Groups > New group: Security, name "' + $GroupName + '"; Members > Add members > the Govern360 app (' + $ClientId + ').')
}

# ---- Azure sign-in (used by steps 2 to 4) -----------------------------------
function Get-G360Token([string]$Resource) {
  try {
    $a = Get-AzAccessToken -ResourceUrl $Resource -ErrorAction Stop
    $t = $a.Token
    if ($t -is [System.Security.SecureString]) { $t = [System.Net.NetworkCredential]::new('', $t).Password }
    return [string]$t
  } catch { return $null }
}
$AzReady = $false
try {
  Import-Module Az.Accounts -ErrorAction Stop
  if (-not (Get-AzContext)) {
    if ($InCloudShell) { try { Connect-AzAccount -Identity -ErrorAction Stop | Out-Null } catch { } }
    if (-not (Get-AzContext)) { Connect-AzAccount -Tenant $EntraTenantId -UseDeviceAuthentication -ErrorAction Stop | Out-Null }
  }
  $AzReady = [bool](Get-AzContext)
} catch { Write-Host ('    Azure PowerShell sign-in did not complete (' + (Short $_) + ').') -ForegroundColor Yellow }
$FabricToken = $null
if ($AzReady) {
  $FabricToken = Get-G360Token 'https://api.fabric.microsoft.com'
  if (-not $FabricToken) {
    try {
      Connect-AzAccount -Tenant $EntraTenantId -UseDeviceAuthentication -ErrorAction Stop | Out-Null
      $FabricToken = Get-G360Token 'https://api.fabric.microsoft.com'
    } catch { }
  }
}
$FabricHeaders = @{}
if ($FabricToken) { $FabricHeaders = @{ Authorization = ('Bearer ' + $FabricToken) } }

# ---- 2. Fabric tenant settings (Preview API) --------------------------------
Write-Host ''
Write-Host '-> 2. Fabric tenant settings (uses a Microsoft Preview API)' -ForegroundColor Cyan
$PortalSettings = 'Fabric admin portal (app.fabric.microsoft.com > Settings > Admin portal) > Tenant settings'
function Set-G360Setting($Setting, [string]$Label, [string]$Where) {
  if (-not $Setting) {
    Note ('2 ' + $Label) 'MANUAL' 'setting not found by its title.'
    Manual ($PortalSettings + ' > ' + $Where + ' > "' + $Label + '": Enabled, Specific security groups, add "' + $GroupName + '".')
    return
  }
  $enabledGroups = @($Setting.enabledSecurityGroups | Where-Object { $_ })
  $excludedGroups = @($Setting.excludedSecurityGroups | Where-Object { $_ })
  if ($GroupId -and ($excludedGroups | Where-Object { $_.graphId -eq $GroupId })) {
    Note ('2 ' + $Label) 'MANUAL' 'the group is in the setting''s "except" list; not changed.'
    Manual ($PortalSettings + ' > ' + $Where + ' > "' + $Setting.title + '": remove "' + $GroupName + '" from the except list.')
    return
  }
  if ($Setting.enabled -and $enabledGroups.Count -eq 0) { Note ('2 ' + $Label) 'OK' 'already enabled for the whole organisation.'; return }
  if ($Setting.enabled -and $GroupId -and ($enabledGroups | Where-Object { $_.graphId -eq $GroupId })) { Note ('2 ' + $Label) 'OK' 'already enabled for the group.'; return }
  if (-not $GroupId -or $Setting.canSpecifySecurityGroups -eq $false) {
    Note ('2 ' + $Label) 'MANUAL' 'needs the group from step 1.'
    Manual ($PortalSettings + ' > ' + $Where + ' > "' + $Setting.title + '": Enabled, Specific security groups, add "' + $GroupName + '".')
    return
  }
  $list = New-Object System.Collections.ArrayList
  if ($Setting.enabled) { foreach ($g in $enabledGroups) { [void]$list.Add(@{ graphId = $g.graphId; name = $g.name }) } }
  [void]$list.Add(@{ graphId = $GroupId; name = $GroupName })
  $body = @{ enabled = $true; enabledSecurityGroups = @($list) }
  if ($excludedGroups.Count -gt 0) { $body.excludedSecurityGroups = @($excludedGroups | ForEach-Object { @{ graphId = $_.graphId; name = $_.name } }) }
  foreach ($k in @('delegateToCapacity', 'delegateToDomain', 'delegateToWorkspace')) { if ($null -ne $Setting.$k) { $body[$k] = $Setting.$k } }
  if ($Setting.properties) { $body.properties = @($Setting.properties) }
  try {
    $uri = $FabricApi + '/admin/tenantsettings/' + [uri]::EscapeDataString([string]$Setting.settingName) + '/update'
    Invoke-RestMethod -Method Post -Uri $uri -Headers $FabricHeaders -ContentType 'application/json' -Body ($body | ConvertTo-Json -Depth 8) -ErrorAction Stop | Out-Null
    $kept = $list.Count - 1
    if ($kept -gt 0) { Note ('2 ' + $Label) 'DONE' ('enabled for the group; the ' + $kept + ' group(s) already there were kept.') }
    else { Note ('2 ' + $Label) 'DONE' 'enabled for the group only.' }
  } catch {
    Note ('2 ' + $Label) 'MANUAL' ('Fabric did not accept the change (' + (Short $_) + ')')
    Manual ($PortalSettings + ' > ' + $Where + ' > "' + $Setting.title + '": Enabled, Specific security groups, add "' + $GroupName + '".')
  }
}
$AllSettings = @()
if ($FabricToken) {
  try {
    $next = $FabricApi + '/admin/tenantsettings'
    while ($next) {
      $page = Invoke-RestMethod -Method Get -Uri $next -Headers $FabricHeaders -ErrorAction Stop
      if ($page.value) { $AllSettings += @($page.value) } elseif ($page.tenantSettings) { $AllSettings += @($page.tenantSettings) }
      $next = $null
      if ($page.continuationUri) { $next = [string]$page.continuationUri }
    }
  } catch { Write-Host ('    Could not read the tenant settings (' + (Short $_) + '). You need the Fabric administrator role.') -ForegroundColor Yellow }
}
if ($AllSettings.Count -gt 0) {
  $public = $AllSettings | Where-Object { $_.title -match 'service principal' -and ($_.title -match 'public api' -or ($_.title -match 'fabric api' -and $_.title -notmatch 'admin')) } | Select-Object -First 1
  $adminRead = $AllSettings | Where-Object { $_.title -match 'service principal' -and $_.title -match 'admin api' -and $_.title -match 'read[- ]?only' } | Select-Object -First 1
  Set-G360Setting $public 'Service principals can call Fabric public APIs' 'Developer settings'
  Set-G360Setting $adminRead 'Service principals can access read-only admin APIs' 'Admin API settings'
} else {
  Note '2 Tenant settings' 'MANUAL' 'Fabric tenant settings could not be read with your sign-in.'
  Manual ($PortalSettings + ' > Developer settings > "Service principals can call Fabric public APIs": Enabled, Specific security groups, add "' + $GroupName + '".')
  Manual ($PortalSettings + ' > Admin API settings > "Service principals can access read-only admin APIs": Enabled, Specific security groups, add "' + $GroupName + '".')
}

# ---- 3. Capacity role (manual) ----------------------------------------------
Write-Host ''
Write-Host '-> 3. Capacity role (by hand - Govern360 never changes capacity administrators)' -ForegroundColor Cyan
$caps = @()
if ($FabricToken) {
  try { $caps = @((Invoke-RestMethod -Method Get -Uri ($FabricApi + '/capacities') -Headers $FabricHeaders -ErrorAction Stop).value | Where-Object { $_ }) } catch { $caps = @() }
}
if ($caps.Count -gt 0) {
  foreach ($c in $caps) {
    Manual ('Fabric admin portal > Capacity settings > Fabric capacity > "' + $c.displayName + '" (' + $c.sku + ', ' + $c.region + ') > Contributor permissions > add "' + $GroupName + '".')
  }
  Note '3 Capacity role' 'MANUAL' ([string]$caps.Count + ' capacity(ies) listed above: add the group as Contributor on each.')
} else {
  Manual ('Fabric admin portal > Capacity settings > Fabric capacity > each capacity > Contributor permissions > add "' + $GroupName + '".')
  Note '3 Capacity role' 'MANUAL' 'no capacities visible to your sign-in; add the group as Contributor on each capacity.'
}

# ---- 4. Azure cost ----------------------------------------------------------
Write-Host ''
Write-Host '-> 4. Azure cost (Cost Management Reader for the Govern360 Azure connection)' -ForegroundColor Cyan
if (-not ($AzureAppId -match $GuidPattern)) {
  $AzureAppId = (Read-Host 'App (client) ID of your Govern360 Azure connection (Connections page), or press Enter to skip').Trim()
}
if (-not ($AzureAppId -match $GuidPattern)) {
  Note '4 Azure cost' 'SKIPPED' 'no Azure connection app ID given. Govern360 shows capacity units and marks dollars as estimates.'
} elseif (-not $AzReady) {
  Note '4 Azure cost' 'MANUAL' 'Azure PowerShell is not signed in.'
  Manual ('Azure portal > Subscriptions > (the subscription with your Fabric capacity) > Access control (IAM) > Add role assignment > Cost Management Reader > ' + $AzureAppId + '.')
} else {
  try {
    Import-Module Az.Resources -ErrorAction Stop
    $azSp = Get-AzADServicePrincipal -ApplicationId $AzureAppId -ErrorAction Stop | Select-Object -First 1
    if (-not $azSp) { throw 'That Azure connection app has no service principal in this tenant.' }
    $orig = Get-AzContext
    $found = 0
    foreach ($sub in @(Get-AzSubscription -TenantId $EntraTenantId -ErrorAction Stop | Where-Object { $_.State -eq 'Enabled' })) {
      Set-AzContext -SubscriptionId $sub.Id -TenantId $EntraTenantId -ErrorAction Stop | Out-Null
      $fab = @(Get-AzResource -ResourceType 'Microsoft.Fabric/capacities' -ErrorAction SilentlyContinue)
      if ($fab.Count -eq 0) { continue }
      $found++
      $scope = '/subscriptions/' + $sub.Id
      $has = @(Get-AzRoleAssignment -ObjectId $azSp.Id -RoleDefinitionName 'Cost Management Reader' -Scope $scope -ErrorAction SilentlyContinue)
      if ($has.Count -gt 0) { Note ('4 Azure cost ' + $sub.Name) 'OK' 'Cost Management Reader already assigned.'; continue }
      try {
        New-AzRoleAssignment -ObjectId $azSp.Id -RoleDefinitionName 'Cost Management Reader' -Scope $scope -ErrorAction Stop | Out-Null
        Note ('4 Azure cost ' + $sub.Name) 'DONE' 'Cost Management Reader assigned.'
      } catch {
        Note ('4 Azure cost ' + $sub.Name) 'MANUAL' ('not assigned (' + (Short $_) + ')')
        Manual ('New-AzRoleAssignment -ApplicationId ' + $AzureAppId + ' -RoleDefinitionName "Cost Management Reader" -Scope ' + $scope + '   (needs Owner or User Access Administrator)')
      }
    }
    if ($orig) { Set-AzContext -Context $orig -ErrorAction SilentlyContinue | Out-Null }
    if ($found -eq 0) { Note '4 Azure cost' 'SKIPPED' 'no subscription you can see holds a Fabric capacity.' }
  } catch {
    Note '4 Azure cost' 'MANUAL' ('could not finish (' + (Short $_) + ')')
    Manual ('Azure portal > Subscriptions > (the subscription with your Fabric capacity) > Access control (IAM) > Add role assignment > Cost Management Reader > ' + $AzureAppId + '.')
  }
}

# ---- 5. Result --------------------------------------------------------------
Write-Host ''
Write-Host '==== Result ====' -ForegroundColor Cyan
foreach ($k in $Results.Keys) { Write-Host ('  ' + $k + ': ' + $Results[$k]) }
Write-Host ''
Write-Host '  5. Now open Govern360 > Copilot Consumption > Fabric > Set up Fabric and click "Check again".' -ForegroundColor Green
Write-Host '     Fabric settings can take up to 15 minutes to apply; if a step still shows Missing, check again later.'
Write-Host '     Nothing here is needed for the rest of Govern360 to work.'
}
`;
}
